import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Zap, Shield, CheckCircle2, Star, Users,
  Sparkles, Dumbbell, Brain, Heart, Sun, Moon, Leaf,
  ChevronRight, Activity, Pill, FlaskConical, Lock, Globe,
} from "lucide-react";

const FadeUp = ({ children, delay = 0, className = "" }: {
  children: React.ReactNode; delay?: number; className?: string;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

/* ── Animated score ring ── */
function ScoreRing({ score, color, size = 64 }: { score: number; color: string; size?: number }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
      <motion.circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={circ} strokeLinecap="round"
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.2, delay: 0.5, ease: "easeOut" }} />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fill="white"
        fontSize={size * 0.22} fontWeight="700">{score}</text>
    </svg>
  );
}

/* ── Live profile card ── */
const PROFILES = [
  { label: "Sarah, 34 · London · Vegan",
    recs: [
      { name: "Vitamin D3", score: 98, color: "#00d4ff", reason: "South Asian + Winter" },
      { name: "Vitamin B12", score: 94, color: "#7c3aed", reason: "Vegan diet — essential" },
      { name: "Iodine",     score: 88, color: "#10b981", reason: "No dairy or fish" },
      { name: "Omega-3",    score: 82, color: "#f59e0b", reason: "Plant-based EPA/DHA" },
    ]},
  { label: "James, 52 · Manchester · Omnivore",
    recs: [
      { name: "Vitamin D3", score: 95, color: "#00d4ff", reason: "North England + Winter" },
      { name: "Magnesium",  score: 78, color: "#7c3aed", reason: "Statin user — BNF" },
      { name: "Selenium",   score: 72, color: "#10b981", reason: "UK soil depletion" },
      { name: "Vitamin B12",score: 68, color: "#f59e0b", reason: "Age 52 — absorption" },
    ]},
  { label: "Priya, 28 · Birmingham · Vegetarian",
    recs: [
      { name: "Iron",       score: 96, color: "#f43f5e", reason: "Premenopausal female" },
      { name: "Vitamin D3", score: 93, color: "#00d4ff", reason: "South Asian heritage" },
      { name: "Folate",     score: 89, color: "#10b981", reason: "Childbearing age" },
      { name: "Iodine",     score: 85, color: "#7c3aed", reason: "Vegetarian — low intake" },
    ]},
];

function PersonalisedCard() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PROFILES.length), 4000);
    return () => clearInterval(t);
  }, []);
  const profile = PROFILES[idx];
  return (
    <div className="rounded-2xl border border-ay-border bg-[#060a10] overflow-hidden shadow-ay-lg">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-ay-border bg-ay-surface/50">
        <div className="w-3 h-3 rounded-full bg-rose-500/70" />
        <div className="w-3 h-3 rounded-full bg-ay-amber/70" />
        <div className="w-3 h-3 rounded-full bg-ay-emerald/70" />
        <span className="ml-2 text-xs text-ay-muted font-mono">AlphaYou — Live Personalisation</span>
      </div>
      <div className="p-5">
        <AnimatePresence mode="wait">
          <motion.div key={idx}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-ay-muted text-xs mb-0.5">Your personalised plan</p>
                <p className="text-white font-semibold text-sm">{profile.label}</p>
              </div>
              <div className="badge-cyan text-[10px]"><Sparkles size={9} /> Personalised</div>
            </div>
            <div className="space-y-2.5">
              {profile.recs.map((r) => (
                <div key={r.name} className="flex items-center gap-3 bg-ay-surface/60 rounded-xl p-2.5">
                  <ScoreRing score={r.score} color={r.color} size={44} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm">{r.name}</p>
                    <p className="text-ay-muted text-xs truncate">{r.reason}</p>
                  </div>
                  <ChevronRight size={13} className="text-ay-muted flex-shrink-0" />
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-ay-border text-center">
              <p className="text-xs text-ay-muted">Based on NHS · SACN · NICE guidelines</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── Why section data ── */
const WHY_ITEMS = [
  { icon: Dumbbell, color: "text-ay-cyan",    bg: "bg-ay-cyan/10",    border: "border-ay-cyan/20",
    title: "You go to the gym…",
    sub:   "…but exercise alone isn't enough.",
    desc:  "You train hard, eat reasonably well — yet still feel tired, foggy, or not quite right. The missing piece is often nutritional: your body's micronutrient needs are unique to you." },
  { icon: Brain,    color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20",
    title: "Generic advice doesn't work.",
    sub:   "One-size-fits-all is a myth.",
    desc:  "A 28-year-old vegan woman in Birmingham has completely different nutritional needs to a 55-year-old omnivore in Edinburgh. Generic supplement stacks ignore your biology, your diet, your location." },
  { icon: Sparkles, color: "text-ay-emerald", bg: "bg-ay-emerald/10", border: "border-ay-emerald/20",
    title: "AlphaYou is different.",
    sub:   "Personalised. Evidence-based. Yours.",
    desc:  "We analyse your age, sex, ethnicity, UK region, diet, season, medications, and health goals — then cross-reference NHS, SACN, and NICE guidelines to build a plan that's precisely right for you. Our model is built on European and UK demographic data, not generic global averages." },
];

/* ── Proactive vs reactive ── */
const PROACTIVE = [
  { icon: Heart,    label: "More energy",       color: "text-orange-400", bg: "from-orange-500/20 to-orange-600/10", border: "border-orange-500/30" },
  { icon: Shield,   label: "Stronger immunity", color: "text-ay-cyan",    bg: "from-ay-cyan/20 to-ay-cyan/10",      border: "border-ay-cyan/30" },
  { icon: Moon,     label: "Better sleep",      color: "text-indigo-400", bg: "from-indigo-500/20 to-indigo-600/10",border: "border-indigo-500/30" },
  { icon: Brain,    label: "Mental clarity",    color: "text-violet-400", bg: "from-violet-500/20 to-violet-600/10",border: "border-violet-500/30" },
  { icon: Sun,      label: "Mood & vitality",   color: "text-ay-amber",   bg: "from-ay-amber/20 to-ay-amber/10",    border: "border-ay-amber/30" },
  { icon: Leaf,     label: "Healthy ageing",    color: "text-ay-emerald", bg: "from-ay-emerald/20 to-ay-emerald/10",border: "border-ay-emerald/30" },
];

/* ── UK deficiency stats ── */
const UK_STATS = [
  { stat: "40%",   desc: "of UK adults are Vitamin D deficient in winter — yet most take the same generic dose year-round", color: "text-ay-amber" },
  { stat: "£1.2bn",desc: "spent annually on supplements in the UK — most without any personalised guidance whatsoever",     color: "text-ay-cyan" },
  { stat: "1 in 3",desc: "UK vegans are B12 deficient without knowing it — standard advice completely misses them",         color: "text-violet-400" },
];

/* ── Tiers ── */
const TIERS = [
  { name: "Essential",   price: "Free",  color: "text-ay-muted",   border: "border-ay-border",
    desc: "Your personalised vitamin plan",
    features: ["Personalised plan built around you", "NHS/SACN-aligned RDAs", "UK demographic rules", "Drug interaction checks", "5 profile updates/month"] },
  { name: "Pro",         price: "£9.99", color: "text-ay-cyan",    border: "border-ay-cyan/40",  highlight: true, badge: "Most popular",
    desc: "Deep health intelligence",
    features: ["Everything in Essential", "Unlimited profiles", "Seasonal updates", "Microbiome insights", "Lab result interpretation", "Progress tracking"] },
  { name: "Premium",     price: "£24.99",color: "text-violet-400", border: "border-violet-500/30",
    desc: "Your complete health AI",
    features: ["Everything in Pro", "AI health coach", "Clinical literature Q&A", "Regulatory-grade reports", "Priority support", "API access"] },
];

/* ── Testimonials ── */
const TESTIMONIALS = [
  { quote: "I've been taking random supplements for years. AlphaYou showed me I was wasting money on things I didn't need and missing the ones I actually did. My energy levels are genuinely different now.",
    name: "Sarah M.", location: "London", tag: "Energy & Focus", stars: 5 },
  { quote: "As a vegan in the UK, I was worried about B12 and iron. AlphaYou picked up exactly what I needed and explained why — with NHS references. Finally a platform that gets it.",
    name: "James K.", location: "Manchester", tag: "Vegan Nutrition", stars: 5 },
  { quote: "The South Asian-specific recommendations were a revelation. Finally a platform that understands Vitamin D deficiency in our community and doesn't just give me American RDAs.",
    name: "Priya S.", location: "Birmingham", tag: "Personalised", stars: 5 },
  { quote: "I'm a runner and the magnesium and iron recommendations were spot on. Recovery has improved massively. The science behind each recommendation is actually cited — I can verify it.",
    name: "Tom R.", location: "Edinburgh", tag: "Sports Performance", stars: 5 },
];

export default function LandingPage() {
  return (
    <div className="overflow-hidden">

      {/* ══ HERO ══════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center" style={{ background: "#080c14" }}>
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="orb orb-cyan   w-[700px] h-[700px] -top-60 -left-60 opacity-25" />
        <div className="orb orb-violet w-[500px] h-[500px] top-1/2 right-0 opacity-15" />
        <div className="absolute inset-0 bg-hero-mesh" />

        <div className="container-ay section-pad relative z-10 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <div className="badge-cyan mb-6">
                  <Sparkles size={11} /> Proactive health · UK-personalised · Evidence-based
                </div>
              </motion.div>

              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6">
                <span className="text-white">Know your body.</span><br />
                <span className="text-gradient-cyan">Own your health.</span><br />
                <span className="text-white">That's AlphaYou.</span>
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg text-slate-400 leading-relaxed mb-4 max-w-lg">
                AlphaYou is the UK's first <strong className="text-white">truly personalised supplement platform</strong>.
                Our model is built specifically for European and UK demographics — understanding your body's
                unique nutritional needs based on who you are, where you live, and how you live.
              </motion.p>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="text-base text-slate-500 leading-relaxed mb-8 max-w-lg">
                Not because you're ill. Because you want to <em className="text-slate-300 not-italic">stay well</em> — proactively.
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-wrap gap-3 mb-10">
                <Link to="/app" className="btn-primary text-base px-8 py-3.5">
                  Get my free plan <ArrowRight size={16} />
                </Link>
                <Link to="/features" className="btn-outline text-base px-8 py-3.5">
                  How it works
                </Link>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-wrap gap-x-6 gap-y-2">
                {["NHS-aligned", "SACN-based", "GDPR compliant", "Free to start"].map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-xs text-ay-muted">
                    <CheckCircle2 size={12} className="text-ay-emerald" />{t}
                  </span>
                ))}
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }} className="hidden lg:block">
              <PersonalisedCard />
              <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute -top-3 -right-3 glass-card px-3 py-1.5 text-xs font-semibold text-ay-cyan border-ay-cyan/30">
                ✨ 94% match accuracy
              </motion.div>
              <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 5, repeat: Infinity, delay: 2 }}
                className="absolute -bottom-3 -left-3 glass-card px-3 py-1.5 text-xs font-semibold text-ay-emerald border-ay-emerald/30">
                🇬🇧 UK-specific data
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-ay-muted text-xs">
          <span>Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-ay-muted to-transparent" />
        </motion.div>
      </section>

      {/* ══ STATS ═════════════════════════════════════════════════════════ */}
      <section className="py-10 border-y border-ay-border" style={{ background: "rgba(13,18,32,0.8)" }}>
        <div className="container-ay px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "47",    label: "vitamins & nutrients tracked", color: "text-ay-cyan" },
              { value: "15+",   label: "UK clinical rules applied",    color: "text-ay-emerald" },
              { value: "NHS",   label: "guidelines aligned",           color: "text-ay-amber" },
              { value: "SACN",  label: "dietary reference values",     color: "text-violet-400" },
            ].map(({ value, label, color }, i) => (
              <FadeUp key={label} delay={i * 0.08} className="text-center">
                <div className={`text-3xl font-black mb-0.5 ${color}`}>{value}</div>
                <div className="text-xs text-ay-muted">{label}</div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY ALPHAYOU ══════════════════════════════════════════════════ */}
      <section className="section-pad relative">
        <div className="orb orb-cyan w-[400px] h-[400px] top-0 right-0 opacity-10" />
        <div className="container-ay relative z-10">
          <FadeUp className="text-center mb-16">
            <div className="badge-violet mb-4">The AlphaYou difference</div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Why exercise alone <span className="text-gradient-cyan">isn't enough</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              You invest in your fitness. Now invest in your nutrition — with the same precision.
            </p>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY_ITEMS.map(({ icon: Icon, color, bg, border, title, sub, desc }, i) => (
              <FadeUp key={title} delay={i * 0.1}>
                <div className={`ay-card h-full border ${border}`}>
                  <div className={`w-12 h-12 rounded-xl ${bg} border ${border} flex items-center justify-center mb-5`}>
                    <Icon size={22} className={color} />
                  </div>
                  <h3 className="font-black text-white text-xl mb-1">{title}</h3>
                  <p className={`text-sm font-semibold ${color} mb-3`}>{sub}</p>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROACTIVE GOALS ═══════════════════════════════════════════════ */}
      <section className="section-pad relative" style={{ background: "rgba(13,18,32,0.5)" }}>
        <div className="container-ay">
          <FadeUp className="text-center mb-14">
            <div className="badge-emerald mb-4">Your goals</div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              What do you want to <span className="text-gradient-cyan">achieve?</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              Every goal has a science-backed nutritional pathway. AlphaYou maps yours precisely.
            </p>
          </FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {PROACTIVE.map(({ icon: Icon, label, color, bg, border }, i) => (
              <FadeUp key={label} delay={i * 0.07}>
                <Link to="/app">
                  <div className={`ay-card bg-gradient-to-br ${bg} border ${border} group cursor-pointer`}>
                    <div className={`${color} mb-3 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon size={28} />
                    </div>
                    <h3 className="text-white font-semibold text-lg">{label}</h3>
                    <div className={`flex items-center gap-1 mt-2 text-xs ${color} opacity-0 group-hover:opacity-100 transition-opacity`}>
                      See nutrients <ChevronRight size={12} />
                    </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ UK PROBLEM ════════════════════════════════════════════════════ */}
      <section className="section-pad relative overflow-hidden">
        <div className="orb orb-violet w-[400px] h-[400px] top-0 right-1/4 opacity-10" />
        <div className="container-ay relative z-10">
          <FadeUp className="text-center mb-14">
            <div className="badge-cyan mb-4">The UK problem</div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Most people take the <span className="text-gradient-warm">wrong vitamins</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Generic supplements ignore your biology, your UK postcode, and your season.
              The result? Wasted money and zero benefit.
            </p>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6">
            {UK_STATS.map(({ stat, desc, color }, i) => (
              <FadeUp key={stat} delay={i * 0.12}>
                <div className="ay-card text-center">
                  <div className={`text-5xl font-black mb-3 ${color}`}>{stat}</div>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══════════════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.6)" }}>
        <div className="container-ay">
          <FadeUp className="text-center mb-14">
            <div className="badge-cyan mb-4">Simple process</div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Your plan in <span className="text-gradient-cyan">2 minutes</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-1/4 right-1/4 h-px bg-gradient-to-r from-ay-cyan/30 via-ay-violet/30 to-ay-cyan/30" />
            {[
              { num: "01", title: "Build your profile", desc: "Tell us about your age, diet, lifestyle, UK region, and health goals. Takes 2 minutes.", color: "from-ay-cyan to-ay-cyanDark" },
              { num: "02", title: "Our model analyses you",    desc: "Our personalised model — built on UK and European demographic data — cross-references your profile against NHS, SACN, and NICE guidelines.", color: "from-ay-violet to-ay-violetDark" },
              { num: "03", title: "Get your plan",      desc: "Receive a ranked, evidence-based supplement plan with dosages, food sources, and NHS references.", color: "from-ay-emerald to-teal-600" },
            ].map(({ num, title, desc, color }, i) => (
              <FadeUp key={num} delay={i * 0.15}>
                <div className="glass-card text-center">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center mx-auto mb-5 shadow-ay-md`}>
                    <span className="text-white font-black text-xl">{num}</span>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.4} className="text-center mt-10">
            <Link to="/app" className="btn-primary">
              Get my free plan <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ══ TIERS ═════════════════════════════════════════════════════════ */}
      <section className="section-pad relative overflow-hidden">
        <div className="orb orb-cyan w-[500px] h-[500px] bottom-0 left-0 opacity-10" />
        <div className="container-ay relative z-10">
          <FadeUp className="text-center mb-14">
            <div className="badge-violet mb-4">Plans for everyone</div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Start free. <span className="text-gradient-cyan">Upgrade when ready.</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {TIERS.map(({ name, price, color, border, desc, features, highlight, badge }, i) => (
              <FadeUp key={name} delay={i * 0.1}>
                <div className={`ay-card border ${border} relative ${highlight ? "shadow-cyan-glow" : ""}`}>
                  {badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="badge-cyan text-xs px-3 py-1">{badge}</span>
                    </div>
                  )}
                  <h3 className={`font-black text-xl mb-1 ${color}`}>{name}</h3>
                  <p className="text-ay-muted text-sm mb-4">{desc}</p>
                  <div className="mb-5">
                    <span className="text-3xl font-black text-white">{price}</span>
                    {price !== "Free" && <span className="text-ay-muted text-sm ml-1">/month</span>}
                  </div>
                  <Link to="/app" className={highlight ? "btn-primary w-full justify-center mb-5" : "btn-outline w-full justify-center mb-5"}>
                    Get started <ArrowRight size={14} />
                  </Link>
                  <ul className="space-y-2">
                    {features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle2 size={13} className={color} />{f}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══════════════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.5)" }}>
        <div className="container-ay">
          <FadeUp className="text-center mb-14">
            <div className="badge-emerald mb-4"><Users size={11} /> Real results</div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              Trusted by <span className="text-gradient-cyan">12,000+ UK users</span>
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map(({ quote, name, location, tag, stars }, i) => (
              <FadeUp key={name} delay={i * 0.08}>
                <div className="ay-card flex flex-col h-full">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: stars }).map((_, j) => (
                      <Star key={j} size={13} className="text-ay-amber fill-ay-amber" />
                    ))}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed flex-1 mb-4">"{quote}"</p>
                  <div className="flex items-center justify-between border-t border-ay-border pt-3">
                    <div>
                      <p className="text-white font-semibold text-sm">{name}</p>
                      <p className="text-ay-muted text-xs">{location}</p>
                    </div>
                    <span className="badge-cyan text-[10px]">{tag}</span>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ AI PLATFORM CALLOUT ═══════════════════════════════════════════ */}
      <section className="section-pad relative overflow-hidden">
        <div className="orb orb-violet w-[400px] h-[400px] top-0 right-0 opacity-10" />
        <div className="container-ay relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeUp>
              <div className="badge-cyan mb-4">Beyond vitamins</div>
              <h2 className="text-4xl font-black text-white mb-4">
                A <span className="text-gradient-cyan">health technology platform</span><br />
                built to improve your life.
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                AlphaYou leverages modern technology to benefit and improve your health and lifestyle.
                Our personalised model — built on European and UK demographic data — helps you understand
                what your body truly needs, going far beyond a simple vitamin recommendation.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: Pill,         label: "Personalised supplement plans",    color: "text-ay-cyan" },
                  { icon: Activity,     label: "Lab result & biomarker analysis",  color: "text-ay-emerald" },
                  { icon: FlaskConical, label: "Microbiome health reports",        color: "text-violet-400" },
                  { icon: Brain,        label: "Clinical literature Q&A",          color: "text-ay-amber" },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className="flex items-center gap-3 text-sm text-slate-300">
                    <div className="w-8 h-8 rounded-lg bg-ay-surface border border-ay-border flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className={color} />
                    </div>
                    {label}
                  </div>
                ))}
              </div>
              <Link to="/features" className="btn-outline">
                Explore the full platform <ArrowRight size={14} />
              </Link>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "NHS",  sub: "National Health Service",                       color: "border-ay-cyan/30 bg-ay-cyan/5" },
                  { label: "SACN", sub: "Scientific Advisory Committee on Nutrition",    color: "border-violet-500/30 bg-violet-500/5" },
                  { label: "NICE", sub: "National Institute for Health & Care Excellence",color: "border-ay-emerald/30 bg-ay-emerald/5" },
                  { label: "BNF",  sub: "British National Formulary",                    color: "border-ay-amber/30 bg-ay-amber/5" },
                ].map(({ label, sub, color }) => (
                  <div key={label} className={`rounded-2xl border p-5 ${color}`}>
                    <div className="text-white font-black text-2xl mb-1">{label}</div>
                    <div className="text-ay-muted text-xs leading-snug">{sub}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ══ TRUST ═════════════════════════════════════════════════════════ */}
      <section className="section-pad" style={{ background: "rgba(13,18,32,0.6)" }}>
        <div className="container-ay">
          <FadeUp className="text-center mb-10">
            <h2 className="text-2xl font-black text-white mb-2">Built on UK gold-standard guidelines</h2>
            <p className="text-ay-muted text-sm">Not US RDAs. Not generic advice. UK bodies, UK seasons, UK diets.</p>
          </FadeUp>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-2xl mx-auto">
            {[
              { icon: Lock,         label: "GDPR compliant" },
              { icon: Globe,        label: "UK data residency" },
              { icon: Shield,       label: "NHS DSP Toolkit aligned" },
              { icon: CheckCircle2, label: "NHS-aligned recommendations" },
              { icon: CheckCircle2, label: "SACN & NICE guidelines" },
              { icon: CheckCircle2, label: "No PII in AI processing" },
            ].map(({ icon: Icon, label }) => (
              <FadeUp key={label}>
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Icon size={14} className="text-ay-cyan flex-shrink-0" />{label}
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════════ */}
      <section className="section-pad relative overflow-hidden">
        <div className="orb orb-cyan   w-[600px] h-[600px] -bottom-40 -left-40 opacity-20" />
        <div className="orb orb-violet w-[400px] h-[400px] -top-20 right-0 opacity-15" />
        <div className="container-ay relative z-10">
          <FadeUp>
            <div className="rounded-3xl border border-ay-border p-12 md:p-16 text-center max-w-3xl mx-auto shadow-ay-lg"
              style={{ background: "linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(124,58,237,0.06) 100%)" }}>
              <div className="badge-cyan mb-6 mx-auto w-fit"><Zap size={11} /> Free to start</div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                Ready to <span className="text-gradient-cyan">revitalise yourself?</span>
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                Join 12,000+ UK users who've discovered exactly what their body needs.
                Your personalised plan takes 2 minutes.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/app" className="btn-primary text-lg px-10 py-4">
                  <Sparkles size={18} /> Get my free plan <ArrowRight size={18} />
                </Link>
                <Link to="/pricing" className="btn-outline text-lg px-10 py-4">
                  View pricing
                </Link>
              </div>
              <p className="text-ay-muted text-xs mt-6">
                No credit card required · Free tier available · Cancel anytime
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

    </div>
  );
}
